import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Drawer,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import {
  FLYOUT_FIELD_TITLE_FONT_SIZE,
  FLYOUT_FIELD_TITLE_FONT_WEIGHT,
} from '@/components/flyouts/constants';
import { useLocalization } from '@/hooks/useLocalization';

interface InfoTooltipProps {
  /** Heading shown at the top of the mobile bottom sheet — typically the field label the icon sits next to (e.g. "Last Updated"). Also interpolated into the button's `aria-label`. Not shown in the desktop tooltip. */
  title: string;
  /** The informational/disclaimer text. Rendered as the tooltip body on desktop and as the sheet body on mobile. */
  content: string;
}

/**
 * Renders an info "ⓘ" affordance next to a field. On desktop it is a hover
 * `Tooltip`; on mobile (Capacitor app or a viewport below the `sm` breakpoint)
 * it is a tappable button that opens a bottom `Drawer`, because hover tooltips
 * are unreachable on touch devices.
 *
 * Related: https://github.com/mui/material-ui/issues/14948
 * Ticket: https://wealthaccess.atlassian.net/browse/EUD-661
 */
export const InfoTooltipDrawer = ({ title, content }: InfoTooltipProps) => {
  const theme = useTheme();
  const { t } = useLocalization();

  const isMobileSizing = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileApp = isCapacitorApp();
  const isMobile = isMobileApp || isMobileSizing;

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  if (isMobile) {
    return (
      <>
        <Button
          aria-label={t('tooltip.label', { label: title })}
          variant='text'
          sx={{
            p: '0 !important',
            minWidth: 0,
            display: 'inline-block',
            ml: 1,
            mt: -0.5,
          }}
          onClick={() => {
            setOpen((prev) => !prev);
          }}
        >
          <InfoIcon
            fontSize='small'
            color='action'
            sx={{ verticalAlign: 'middle' }}
          />
        </Button>

        <Drawer
          anchor='bottom'
          open={open}
          onClose={handleClose}
          variant='temporary'
          slotProps={{
            paper: {
              sx: {
                borderRadius: '12px',
                borderEndStartRadius: 0,
                borderEndEndRadius: 0,
              },
            },
          }}
        >
          <Box
            sx={{
              p: 4,
              pb: 'calc(32px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
              maxHeight: '75vh',
              minHeight: '50vh',
            }}
          >
            <Typography
              fontSize={FLYOUT_FIELD_TITLE_FONT_SIZE}
              fontWeight={FLYOUT_FIELD_TITLE_FONT_WEIGHT}
              gutterBottom
            >
              {title}
            </Typography>
            <Typography pb={8}>{content}</Typography>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Tooltip
      title={content}
      leaveTouchDelay={3000}
      enterTouchDelay={50}
      arrow
    >
      <InfoIcon
        fontSize='small'
        color='action'
        tabIndex={0}
        role='img'
        aria-hidden={false}
        aria-label={t('tooltip.label', { label: title })}
        sx={{ ml: 1, verticalAlign: 'middle' }}
      />
    </Tooltip>
  );
};
